/**
 * Foundry (D441): the organisation's approved Microsoft Foundry connections, the requests people make to have more
 * approved, and the catalogue of Foundry agents they publish. Three places in a secondary nav; each is a header, its
 * figures, then its lists. Connections and requests belong to those who edit policies (`rules.policies`), the catalogue to
 * those who look after specialists (`agents.specialists`). With roles off every administrator sees all three.
 */

import { Navigate, useParams } from 'react-router-dom';

import { AgentsPanel } from '../components/foundry/AgentsPanel.tsx';
import { ConnectionsPanel } from '../components/foundry/ConnectionsPanel.tsx';
import { RequestsPanel } from '../components/foundry/RequestsPanel.tsx';
import { PartBoundary } from '../components/ui/PartBoundary.tsx';
import { SubnavLayout, type SubnavItem } from '../components/ui/Layout.tsx';
import { TONE } from '../components/foundry/tone.ts';
import { holds, sees, usePermissions } from '../permissions.ts';

type Tab = 'connections' | 'requests' | 'agents';

export function FoundryPage() {
  const { tab } = useParams<{ tab?: string }>();
  const perms = usePermissions();
  const p = perms.data;
  const roles = p?.mode === 'roles';
  const policies = !roles || sees(p, 'rules.policies');
  const specialists = !roles || sees(p, 'agents.specialists');
  const shown: Array<SubnavItem & { key: Tab }> = [
    ...(policies ? [{ key: 'connections' as const, label: 'Connections', to: '/foundry' }, { key: 'requests' as const, label: 'Requests', to: '/foundry/requests' }] : []),
    ...(specialists ? [{ key: 'agents' as const, label: 'Agents', to: '/foundry/agents' }] : []),
  ];
  if (perms.loading && !p) return null;
  const current = (tab ?? 'connections') as Tab;
  if (!shown.length) return <Navigate to="/" replace />;
  if (!shown.some((s) => s.key === current)) return <Navigate to={shown[0]!.to} replace />;

  // Roles on, a change needs the verb: the API still decides, but the buttons are not offered to a reader.
  const mayEdit = !roles || holds(p, 'rules.policies', 'update');
  const mayReview = !roles || holds(p, 'agents.specialists', 'update');

  return (
    <SubnavLayout label="Foundry" groups={[{ label: '', items: shown }]} current={current}>
      <PartBoundary key={current} name="Foundry" tone={TONE}>
        {current === 'connections' ? <ConnectionsPanel mayEdit={mayEdit} /> : current === 'requests' ? <RequestsPanel mayEdit={mayEdit} /> : <AgentsPanel mayReview={mayReview} />}
      </PartBoundary>
    </SubnavLayout>
  );
}
